import { PlaceholderImage, Skeleton, Typography } from "@/components/atoms";
import type { TypographyVariant } from "@/components/atoms/Typography";
import { BackArrowIcon } from "@/components/atoms/BackArrowIcon";
import { BuildingOutlineIcon, MapPinnedIcon, ScrollTextIcon } from "@/components/atoms/icons";
import { Accordion } from "@/components/molecules/Accordion";
import { DocumentItem } from "@/components/molecules/DocumentItem";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { downloadAndPersistBrochureFiles } from "@/core/utils/brochureDownload";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import {
  guestProjectDetailProperties,
  trackGuestLanguageChange,
  trackGuestLoginOverlayClick,
  trackGuestProjectBrochureDownloadClick,
  trackGuestProjectCarouselManualChange,
  trackGuestProjectMapOpenClick,
} from '@/core/services/trackDynatraceCatalog';
import { logger } from "@/core/logger";
import { shareFile } from "@/core/wrappers/sharing";
import { GuestListEmptyState } from "@/modules/guest-web/components/GuestListEmptyState";
import { GuestUnitAboutSections } from "@/modules/guest-web/components/GuestUnitAboutSections";
import {
  getGuestDetailsLayout,
  getGuestDetailsTypeScale,
} from "@/modules/guest-web/constants/guestLayout";
import { useGuestLabels } from "@/modules/guest-web/hooks/useGuestLabels";
import { useGuestProjectImages } from "@/modules/guest-web/hooks/useGuestProjectImages";
import { useGuestProjectsQuery } from "@/modules/guest-web/hooks/useGuestProjectsQuery";
import { mapProjectToDetails } from "@/modules/guest-web/services/mapGuestProjects";
import type { GuestListLoadState } from "@/modules/guest-web/types/guest.types";
import { useMobileAmenities } from "@/modules/property-details/hooks/useMobileAmenities";
import type { WebAmenity } from "@/modules/property-details/hooks/useWebAmenities";
import { WebAmenityIcon } from "@/modules/property-details/components/WebAmenityIcon";
import { GUEST_LANGUAGE_TOGGLE_WIDTH, GuestLanguageToggle } from "./GuestLanguageToggle";
import { GuestFloatingLoginButtonFixed } from "./GuestFloatingLoginButtonFixed";
import { GuestLoginPromptDialogMobile } from "./GuestLoginPromptDialogMobile";
import { router, useLocalSearchParams } from "expo-router";
import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type ImageSourcePropType,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const AMENITIES_PER_ROW = 3;

function GuestMobileAmenityRows({
  amenities,
  iconColor,
  labelVariant,
}: Readonly<{
  amenities: readonly WebAmenity[];
  iconColor: string;
  labelVariant: TypographyVariant;
}>) {
  const rows: React.JSX.Element[] = [];
  for (let start = 0; start < amenities.length; start += AMENITIES_PER_ROW) {
    const rowAmenities = amenities.slice(start, start + AMENITIES_PER_ROW);
    rows.push(
      <View key={rowAmenities[0]?.value ?? `amenity-row-${start}`} style={styles.amenitiesRow}>
        {rowAmenities.map(({ value, label, iconUrl }) => (
          <View key={value} style={styles.amenityItem}>
            <WebAmenityIcon iconUrl={iconUrl} label={label} size={24} defaultColor={iconColor} />
            <Typography variant={labelVariant} color="secondary" style={styles.amenityText}>
              {label}
            </Typography>
          </View>
        ))}
      </View>,
    );
  }
  return <>{rows}</>;
}

/**
 * Mobile guest unit details — reuses the same guest-web data primitives
 * (useGuestProjectsQuery + useGuestProjectImages + mapProjectToDetails: same
 * /v1/projects list + Contentful images) and presentational pieces that are
 * already cross-platform (accordion body, login prompt,
 * floating login button). Deliberately does NOT reuse the guest-web
 * useGuestUnitDetails hook wholesale — its `if (!id) router.replace('/guest')`
 * is a web-only fallback that redirects native straight to Welcome (via
 * GuestRouteGate's Platform.OS !== 'web' check) any time the id param hasn't
 * hydrated yet right after navigation; here a missing/unmatched id is just
 * this screen's own empty state. Only the mobile shell (header, hero,
 * Accordion instead of WebAccordion) is new — see WebAccordion's own doc
 * comment: "Does NOT replace the mobile-owned shared Accordion molecule."
 *
 * Documents section: single brochure file downloads as-is
 * ({projectName}-brochure.{format}); multiple files are zipped into one
 * archive first ({projectName}-brochure.zip) — see brochureDownload.ts.
 */
export function GuestUnitDetailsScreenMobile() {
  const colors = useUIColors();
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const labels = useGuestLabels();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getGuestDetailsLayout(width), [width]);
  const typeScale = useMemo(() => getGuestDetailsTypeScale(layout), [layout]);
  // Own id/unit resolution instead of reusing useGuestUnitDetails as a whole —
  // that hook's `if (!id) router.replace('/guest')` is a web-only fallback:
  // `/guest` immediately redirects native to `/welcome` (GuestRouteGate,
  // Platform.OS !== 'web'), so any moment where the id param hasn't hydrated
  // yet after router.push bounced this screen straight to Welcome before it
  // could ever render. Missing/unmatched id is just an empty state here.
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const id = useMemo(() => {
    const raw = Array.isArray(params.id) ? params.id[0] : params.id;
    return raw?.trim() || "";
  }, [params.id]);

  const { projects, isLoading, isError } = useGuestProjectsQuery();
  const projectIds = useMemo(() => projects.map((project) => project.project_id), [projects]);
  const { imagesByProjectId, brochureUrlsByProjectId, nameByProjectId, descriptionByProjectId } =
    useGuestProjectImages(projectIds);

  const unit = useMemo(() => {
    if (!id) {
      return null;
    }
    const match = projects.find((project) => project.project_id === id);
    if (!match) {
      return null;
    }
    const brochureUrls = brochureUrlsByProjectId[match.project_id];
    return mapProjectToDetails(match, imagesByProjectId[match.project_id] ?? [], {
      locale: currentLanguage === "ar" ? "ar" : "en",
      contentfulName: nameByProjectId[match.project_id],
      contentfulDescription: descriptionByProjectId[match.project_id],
      ...(brochureUrls ? { brochureUrls, brochureTitle: labels.brochure } : {}),
    });
  }, [
    id,
    projects,
    imagesByProjectId,
    brochureUrlsByProjectId,
    nameByProjectId,
    descriptionByProjectId,
    currentLanguage,
    labels.brochure,
  ]);

  let loadState: GuestListLoadState = "ready";
  if (!id || isLoading) {
    loadState = "loading";
  } else if (isError) {
    loadState = "error";
  } else if (!unit) {
    loadState = "empty";
  }

  const [loginVisible, setLoginVisible] = useState(false);

  useCatalogDynatraceView(
    DT_SCREEN.GUEST,
    "guest_project_detail_viewed",
    loadState === "ready" && Boolean(unit),
    unit
      ? guestProjectDetailProperties({
          projectId: unit.id,
          projectName: unit.title,
          location: unit.location,
          locale: currentLanguage,
        })
      : undefined,
  );

  const unitImages = useMemo<ImageSourcePropType[]>(
    () => (unit?.images ?? []).map((img) => ({ uri: img.uri })),
    [unit?.images],
  );

  const brochureDocument = unit?.documents?.[0];
  const [isDownloadingBrochure, setIsDownloadingBrochure] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successDialogVisible, setSuccessDialogVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [savedFileUri, setSavedFileUri] = useState<string | null>(null);
  const [savedRequiresShare, setSavedRequiresShare] = useState(false);

  const handleDownloadBrochure = async () => {
    if (!brochureDocument || brochureDocument.urls.length === 0) {
      return;
    }
    if (unit?.id) {
      trackGuestProjectBrochureDownloadClick(unit.id);
    }
    setIsDownloadingBrochure(true);
    try {
      const result = await downloadAndPersistBrochureFiles(
        brochureDocument.urls,
        unit?.title || "Property",
        t as any,
      );
      if (result.success && result.fileUri && result.message) {
        setSuccessMessage(result.message);
        setSavedFileUri(result.fileUri);
        setSavedRequiresShare(Boolean(result.requiresShareToSave));
        setSuccessDialogVisible(true);
      } else {
        setErrorMessage(result.error || t("propertyDetails.documentSaveFailed"));
        setErrorDialogVisible(true);
      }
    } catch (err: any) {
      logger.error("GuestUnitDetailsScreenMobile", "Failed to download brochure", {
        error: err?.message || err,
      });
      setErrorMessage(err?.message || t("propertyDetails.brochureDownloadFailed"));
      setErrorDialogVisible(true);
    } finally {
      setIsDownloadingBrochure(false);
    }
  };

  // Local 3-col grid (icon above label) so this mobile screen matches
  // property-details / Figma — GuestAmenitiesGrid stays 2-col on phone.
  const amenityItems = useMobileAmenities(unit?.amenities);
  const hasAmenities = amenityItems.length > 0;
  const showAboutAccordion = Boolean(
    unit &&
      (typeof unit.unitsCount === "number" ||
        unit.typeTags.length > 0 ||
        hasAmenities),
  );

  const handleMapPress = () => {
    if (!unit?.location) {
      return;
    }
    trackGuestProjectMapOpenClick(unit.id);
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(unit.location)}`;
    Linking.openURL(mapUrl).catch(() => {
      // Non-critical external navigation — ignore failures.
    });
  };

  const goLogin = () => {
    setLoginVisible(false);
    // Clear the guest screens (and the original login screen underneath,
    // reached via "Continue as Guest") off the stack first, same pattern
    // app/login.tsx itself uses on a completed session — so device-back from
    // this fresh login screen lands on Welcome instead of back into guest
    // browsing.
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.replace("/login");
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <View style={[styles.header, { paddingTop: insets.top + 8 }]}>
        {/* Same-width side slots (matching the language toggle's own width)
            as GuestHomeScreenMobile's header — keeps the title centered
            relative to the whole header, not just the leftover space next
            to the toggle. */}
        <View style={styles.headerSideSlot}>
          <Pressable onPress={() => router.back()} hitSlop={8} accessibilityRole="button">
            <BackArrowIcon size={24} />
          </Pressable>
        </View>
        <Typography
          variant="title3"
          color="primary"
          numberOfLines={1}
          style={styles.headerTitle}
        >
          {t("propertyDetails.unitDetails")}
        </Typography>
        <GuestLanguageToggle
          value={currentLanguage}
          onChange={(value) => {
            if (value === currentLanguage) {
              return;
            }
            trackGuestLanguageChange(value, unit?.id);
            void changeLanguage(value);
          }}
        />
      </View>

      {loadState === "loading" && (
        <ScrollView contentContainerStyle={styles.content}>
          <Skeleton height={layout.carouselHeight} borderRadius={16} />
          <Skeleton height={24} width="70%" borderRadius={6} />
          <Skeleton height={16} width="50%" borderRadius={6} />
        </ScrollView>
      )}

      {/* Matches web's GuestUnitDetailsLayoutShell: "error" and "empty" (unit
          not found) both show the same generic fetch-error copy here —
          labels.exploreEmpty ("No other properties to explore right now.")
          is a different string, only for the Explore-sidebar's own empty
          state, not this main-content fallback. */}
      {(loadState === "empty" || loadState === "error") && (
        <GuestListEmptyState title={labels.emptyFetchError} />
      )}

      {loadState === "ready" && unit && (
        <>
          <ScrollView
            contentContainerStyle={[styles.content, { paddingBottom: 100 + insets.bottom }]}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.hero}>
              {unitImages.length > 0 ? (
                <ImageCarousel
                  images={unitImages}
                  height={layout.carouselHeight}
                  activeDotColor={colors.white}
                  onManualIndexChange={(imageIndex) => {
                    trackGuestProjectCarouselManualChange(unit.id, imageIndex);
                  }}
                />
              ) : (
                // Same placeholder as GuestHomeScreenMobile's grid card when
                // a property has no images.
                <View
                  style={[
                    styles.heroPlaceholder,
                    { height: layout.carouselHeight, backgroundColor: colors.backgroundLevel2 },
                  ]}
                >
                  <PlaceholderImage />
                </View>
              )}
            </View>

            <View style={styles.titleBlock}>
              <View style={styles.titleRow}>
                <View style={styles.titleTextGroup}>
                  <Typography variant={typeScale.pageTitle} color="primary" style={styles.titleText}>
                    {unit.title}
                  </Typography>
                  {unit.description?.trim() ? (
                    <Typography variant="caption" color="secondary">
                      {unit.description.trim()}
                    </Typography>
                  ) : null}
                  {unit.location ? (
                    <Typography variant={typeScale.location} color="secondary">
                      {unit.location}
                    </Typography>
                  ) : null}
                </View>
                {unit.location ? (
                  <Pressable onPress={handleMapPress} accessibilityRole="button">
                    <MapPinnedIcon size={typeScale.mapIconSize} color={colors.fontPrimary} />
                  </Pressable>
                ) : null}
              </View>
            </View>

            {showAboutAccordion ? (
              <Accordion icon={BuildingOutlineIcon} title={labels.aboutProperty} defaultExpanded>
                <GuestUnitAboutSections
                  unit={{ ...unit, amenities: [] }}
                  layout={layout}
                  typeScale={typeScale}
                  labels={labels}
                />
                {hasAmenities ? (
                  <View style={styles.amenitiesSection}>
                    <Typography variant={typeScale.aboutHeading} color="primary">
                      {labels.amenities}
                    </Typography>
                    <View style={styles.amenitiesContainer}>
                      <GuestMobileAmenityRows
                        amenities={amenityItems}
                        iconColor={colors.fontPrimary}
                        labelVariant={typeScale.amenityLabel}
                      />
                    </View>
                  </View>
                ) : null}
              </Accordion>
            ) : null}

            {brochureDocument && (
              // Tightened paddings so "Brochure" starts exactly under the
              // "Documents" title: 4 content + 1 border + 8 padding + 20 icon
              // + 8 gap = 41 = Accordion header's 9 padding + 24 icon + 8 gap.
              <Accordion
                icon={ScrollTextIcon}
                title={labels.documents}
                defaultExpanded
                contentPadding={4}
              >
                <View style={styles.documentItemWrapper}>
                  <DocumentItem
                    id={brochureDocument.id}
                    title={brochureDocument.title}
                    type="PDF"
                    downloadUrl=""
                    onPress={() => !isDownloadingBrochure && handleDownloadBrochure()}
                    containerStyle={styles.documentItemBox}
                    contentStyle={styles.documentItemContent}
                  />
                  {isDownloadingBrochure && (
                    <View style={[styles.loadingOverlay, { backgroundColor: colors.backgroundLevel1 }]}>
                      <ActivityIndicator size="small" color={colors.fontPrimary} />
                    </View>
                  )}
                </View>
              </Accordion>
            )}
          </ScrollView>

          <GuestFloatingLoginButtonFixed
            onPress={() => {
              trackGuestLoginOverlayClick(unit.id);
              setLoginVisible(true);
            }}
          />
          <GuestLoginPromptDialogMobile
            visible={loginVisible}
            onMaybeLater={() => setLoginVisible(false)}
            onLogin={goLogin}
          />

          <OkDialog
            visible={errorDialogVisible}
            message={errorMessage}
            onOk={() => setErrorDialogVisible(false)}
          />

          <OkDialog
            visible={successDialogVisible}
            title={t("common.downloadSuccessTitle")}
            message={successMessage}
            okText={Platform.OS === "ios" || savedRequiresShare ? t("common.saveShare") : t("common.ok")}
            onOk={async () => {
              if ((Platform.OS === "ios" || savedRequiresShare) && savedFileUri) {
                const shareResult = await shareFile({ uri: savedFileUri });
                if (!shareResult.success && shareResult.error) {
                  logger.warn("GuestUnitDetailsScreenMobile", "Sharing failed", {
                    error: shareResult.error,
                    fileUri: savedFileUri,
                  });
                }
              }
              setSuccessDialogVisible(false);
              setSavedFileUri(null);
              setSavedRequiresShare(false);
            }}
          />
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  headerSideSlot: {
    width: GUEST_LANGUAGE_TOGGLE_WIDTH,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  headerTitle: {
    flex: 1,
    textAlign: "center",
  },
  content: {
    padding: 16,
    gap: 16,
  },
  hero: {
    borderRadius: 16,
    overflow: "hidden",
  },
  heroPlaceholder: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  titleBlock: {
    gap: 8,
  },
  titleRow: {
    flexDirection: "row",
    // Centers the map pin icon against the combined height of the title +
    // location lines grouped below, instead of only lining up with the
    // title's single line.
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  titleTextGroup: {
    flex: 1,
    gap: 4,
  },
  titleText: {
    flexShrink: 1,
  },
  documentItemWrapper: {
    position: "relative",
  },
  documentItemBox: {
    paddingHorizontal: 8,
  },
  documentItemContent: {
    gap: 8,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    opacity: 0.8,
  },
  amenitiesSection: {
    paddingVertical: 12,
    gap: 16,
  },
  amenitiesContainer: {
    gap: 20,
  },
  amenitiesRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  amenityItem: {
    flex: 1,
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 4,
  },
  amenityText: {
    textAlign: "center",
  },
});

export default GuestUnitDetailsScreenMobile;
